import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { AnalyticsConsentCard } from './analytics/AnalyticsConsent'
import { HomepageV3Footer, HomepageV3Header } from './components/HomepageV3Chrome'
import { clearWeeklyOrderProducts, setWeeklyOrderProducts } from './config/businessData'
import { fetchPublishedMenu } from './config/liveMenu'
import { clearPricingDelivery, fetchPricingDelivery, installPricingDelivery } from './config/livePricingDelivery'
import { cartBoxCount, migrateCartProductIdsForCatalogue } from './order/cartStorage'
import ClosedStoreState from './store/ClosedStoreState'
import { usePublicStoreStatus } from './store/usePublicStoreStatus'
import './styles/order-v3.css'
import './styles/store-status-final-corrections.css'

const OrderPageV3 = lazy(() => import('./order/OrderPageV3'))

type AuthorityState = 'loading' | 'ready' | 'empty' | 'menu-unavailable' | 'pricing-unavailable'

function AuthorityGate({ state }: { state: Exclude<AuthorityState, 'ready'> }) {
  const checking = state === 'loading'
  const wording = checking
    ? "Loading this week's menu"
    : state === 'empty'
      ? 'No flavours are available right now'
      : state === 'pricing-unavailable'
        ? 'Current pricing is temporarily unavailable'
        : "This week's menu is temporarily unavailable"
  const subtext = checking
    ? 'One moment while we load the current flavours and pricing.'
    : state === 'empty'
      ? 'Please check back later for the next published flavour drop.'
      : 'Please refresh in a moment.'
  const badge = checking ? 'Loading' : state === 'empty' ? 'No flavours' : 'Unavailable'
  return (
    <section className="order-v3-store-gate" role="status" aria-live="polite" aria-busy={checking}>
      <span className="order-v3-store-gate__badge">{badge}</span>
      <h1>{wording}</h1>
      <p>{subtext}</p>
    </section>
  )
}

export default function OrderRuntimePage() {
  const [cartCount, setCartCount] = useState(() => cartBoxCount())
  const [authorityState, setAuthorityState] = useState<AuthorityState>('loading')
  const versionRef = useRef('')
  const storeStatus = usePublicStoreStatus()

  const refreshAuthority = useCallback(async () => {
    let menu: Awaited<ReturnType<typeof fetchPublishedMenu>>
    try {
      menu = await fetchPublishedMenu()
    } catch {
      clearWeeklyOrderProducts()
      setAuthorityState('menu-unavailable')
      return
    }

    let pricing: Awaited<ReturnType<typeof fetchPricingDelivery>>
    try {
      pricing = await fetchPricingDelivery()
    } catch {
      clearPricingDelivery()
      setAuthorityState('pricing-unavailable')
      return
    }

    const menuVersion = menu.products.map((product) => `${product.id}:${product.displayOrder}:${product.availability}`).join('|')
    const version = `${menuVersion}::pricing:${pricing.version}`
    if (versionRef.current && versionRef.current !== version) {
      setWeeklyOrderProducts(menu.products)
      migrateCartProductIdsForCatalogue(menu.products)
      installPricingDelivery(pricing)
      window.location.reload()
      return
    }

    setWeeklyOrderProducts(menu.products)
    migrateCartProductIdsForCatalogue(menu.products)
    installPricingDelivery(pricing)
    versionRef.current = version
    setAuthorityState(menu.products.length ? 'ready' : 'empty')
  }, [])

  useEffect(() => {
    void refreshAuthority()
    const timer = window.setInterval(() => void refreshAuthority(), 15_000)
    const onFocus = () => void refreshAuthority()
    const onVisibility = () => { if (document.visibilityState === 'visible') void refreshAuthority() }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [refreshAuthority])

  useEffect(() => {
    const refreshCart = () => setCartCount(cartBoxCount())
    window.addEventListener('storage', refreshCart)
    const timer = window.setInterval(refreshCart, 500)
    return () => {
      window.removeEventListener('storage', refreshCart)
      window.clearInterval(timer)
    }
  }, [])

  if (storeStatus.state !== 'open') {
    return (
      <ClosedStoreState
        variant="order"
        mode={storeStatus.state}
        status={storeStatus.status}
        showOrderProductCopy
      />
    )
  }

  if (authorityState === 'ready') {
    return <Suspense fallback={null}><OrderPageV3 /></Suspense>
  }

  return (
    <div className="sg-v3 order-v3-page">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <HomepageV3Header cartCount={cartCount} activeHref="/order" />
      <main id="main-content" tabIndex={-1}><AuthorityGate state={authorityState} /></main>
      <HomepageV3Footer />
      <AnalyticsConsentCard />
    </div>
  )
}
